import * as Comlink from "comlink";

import type {
  CompleteRuleConfig,
  TypesMap,
  WasmLang,
} from "@acme/ast-grep-wasm-loader";

import type { exposed } from "./codemod-sandbox-worker";

type WorkerLink = Comlink.Remote<typeof exposed>;

// Worker management per idempotency key
interface WorkerInstance {
  link: WorkerLink;
  worker: Worker;
  currentLang: WasmLang;
}

const workersByIdempotencyKey = new Map<string, WorkerInstance>();

// Queue system for handling concurrent calls per idempotency key
interface QueuedCall {
  operation: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

class SkippedCallError extends Error {
  constructor() {
    super("Skipping call");
    this.name = "SkippedCallError";
  }
}

export function isSkippedCallError(error: unknown): error is SkippedCallError {
  return error instanceof SkippedCallError;
}

class CallQueue {
  private queuesByKey = new Map<string, QueuedCall[]>();
  private processingKeys = new Set<string>();

  async enqueue<T>(
    idempotencyKey: string,
    operation: () => Promise<T>,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (!this.queuesByKey.has(idempotencyKey)) {
        this.queuesByKey.set(idempotencyKey, []);
      }

      const queue = this.queuesByKey.get(idempotencyKey);
      if (queue) {
        queue.push({
          operation: operation as () => Promise<unknown>,
          resolve: resolve as (value: unknown) => void,
          reject,
        });
      }
      void this.processQueue(idempotencyKey);
    });
  }

  private async processQueue(idempotencyKey: string) {
    if (this.processingKeys.has(idempotencyKey)) {
      return;
    }

    const queue = this.queuesByKey.get(idempotencyKey);
    if (!queue || queue.length === 0) {
      return;
    }

    this.processingKeys.add(idempotencyKey);

    while (queue.length > 0) {
      const call = queue.shift();
      if (!call) break;

      try {
        const result = await call.operation();
        call.resolve(result);
      } catch (error) {
        call.reject(error);
      }
    }

    this.processingKeys.delete(idempotencyKey);
  }

  clear(idempotencyKey?: string) {
    if (idempotencyKey) {
      // Clear specific key
      const queue = this.queuesByKey.get(idempotencyKey);
      if (queue) {
        this.queuesByKey.delete(idempotencyKey);
      }
      this.processingKeys.delete(idempotencyKey);
    } else {
      // Clear all queues
      this.queuesByKey.forEach((queue) => {
        queue.forEach(({ reject }) => reject(new Error("Worker terminated")));
      });
      this.queuesByKey.clear();
      this.processingKeys.clear();
    }
  }
}

const callQueue = new CallQueue();

function terminateWorker(idempotencyKey?: string) {
  if (idempotencyKey) {
    // Terminate specific worker
    const workerInstance = workersByIdempotencyKey.get(idempotencyKey);
    if (workerInstance) {
      workerInstance.worker.terminate();
      workersByIdempotencyKey.delete(idempotencyKey);
      callQueue.clear(idempotencyKey);
    }
  } else {
    // Terminate all workers
    workersByIdempotencyKey.forEach((instance) => {
      instance.worker.terminate();
    });
    workersByIdempotencyKey.clear();
    callQueue.clear();
  }
}

function initLink(idempotencyKey: string, lang: WasmLang): WorkerLink {
  const existingInstance = workersByIdempotencyKey.get(idempotencyKey);

  // If we have an existing worker for this key but different language, terminate it
  if (existingInstance && existingInstance.currentLang !== lang) {
    terminateWorker(idempotencyKey);
  }

  // If we have a valid worker for this key and language, return it
  if (existingInstance && existingInstance.currentLang === lang) {
    return existingInstance.link;
  }

  // Create new worker for this idempotency key
  const worker = new Worker(
    new URL("./codemod-sandbox-worker", import.meta.url),
    {
      type: "module",
    },
  );

  // Set up error handling for worker crashes
  worker.addEventListener("error", (event) => {
    console.error(
      `[EXECUTOR] Worker error for ${idempotencyKey}:`,
      event.error,
    );
    terminateWorker(idempotencyKey);
  });

  const link = Comlink.wrap(worker) as WorkerLink;

  workersByIdempotencyKey.set(idempotencyKey, {
    link,
    worker,
    currentLang: lang,
  });

  return link;
}

const callCountsByIdempotencyKey = new Map<string, number>();

// Helper function to execute operations with worker crash detection
async function executeWithWorkerProtection<T>(
  idempotencyKey: string,
  operation: (link: WorkerLink) => Promise<T>,
  lang: WasmLang,
  maxRetries = 2,
): Promise<T> {
  let lastError: unknown;
  let currentCallId = callCountsByIdempotencyKey.get(idempotencyKey) ?? 0;
  callCountsByIdempotencyKey.set(idempotencyKey, ++currentCallId);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if ((callCountsByIdempotencyKey.get(idempotencyKey) ?? 0) > currentCallId) {
      throw new SkippedCallError();
    }
    try {
      const link = initLink(idempotencyKey, lang);

      // Execute the operation directly - we rely on worker event listeners for crash detection
      // No timeout needed since worker crashes are detected via error events
      const result = await operation(link);
      return result;
    } catch (error) {
      lastError = error;

      // Check if this looks like a worker crash
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isWorkerCrash =
        errorMessage.includes("Worker") ||
        errorMessage.includes("terminated") ||
        errorMessage.includes("crashed") ||
        errorMessage.includes("unreachable") ||
        errorMessage.includes("RuntimeError");

      if (isWorkerCrash || attempt < maxRetries) {
        console.log(
          `[EXECUTOR] ${isWorkerCrash ? "Worker crash detected" : "Retrying"} for ${idempotencyKey}, recreating worker...`,
        );
        terminateWorker(idempotencyKey);

        // Wait a bit before retrying
        await new Promise((resolve) =>
          setTimeout(resolve, 100 * (attempt + 1)),
        );
      }
    }
  }

  throw lastError;
}

export async function executeCode(
  code: string,
  sourceCode: string,
  lang: WasmLang,
): Promise<Awaited<ReturnType<WorkerLink["executeCode"]>>> {
  return callQueue.enqueue("executeCode", () =>
    executeWithWorkerProtection(
      "executeCode",
      (link) =>
        link.executeCode(code, sourceCode, lang, window.location.origin),
      lang,
      1,
    ),
  );
}

export async function processRuleConfig<M extends TypesMap>(
  sourceCode: string,
  rules: CompleteRuleConfig<M>[],
  lang: WasmLang,
) {
  return callQueue.enqueue("processRuleConfig", () =>
    executeWithWorkerProtection(
      "processRuleConfig",
      (link) =>
        link.processRuleConfig(
          sourceCode,
          rules as unknown as CompleteRuleConfig<TypesMap>[],
          lang,
          window.location.origin,
        ),
      lang,
    ),
  );
}

export async function dumpASTNodes(lang: WasmLang, sourceCode: string) {
  return callQueue.enqueue("dumpASTNodes", () =>
    executeWithWorkerProtection(
      "dumpASTNodes",
      (link) => link.dumpASTNodes(lang, sourceCode, window.location.origin),
      lang,
    ),
  );
}

export { terminateWorker };
